import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzIbDataService,
  Herinvesteringsreserve,
  IncomeTaxEntity,
  Onderneming
} from '@snelstart-ventures/fiscaal/domain-logic';
import {filter, Observable, shareReplay, take, tap} from 'rxjs';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-hir-ib',
  templateUrl: './spec-hir.component.html',
  imports: [
    SharedUiAlertModule,
    SharedUiDialogModule,
    SharedUiButtonModule,
    ReactiveFormsModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    CommonModule
  ],
  styleUrls: ['./spec-hir.component.scss']
})
export class SpecHirComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  public dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  public indexOnderneming = 0;
  public dialogVisible = false;
  public isPartner = false;
  public jaren: SelectFormControlItem<number>[] = [];

  public formGroup = new FormGroup({
    waardeOpening: new FormControl<number | null>(null),
    waardeSlot: new FormControl<number | null>(null),
    dotatieHIR: new FormControl<number | null>(null),
    vrijvalHIR: new FormControl<number | null>(null),
  })

  public specificatieFg = new FormGroup({
    specHerinvesteringsreserve: new FormArray<
      FormGroup<{
        omschrijving: FormControl<string | null>;
        jaarVanVervreemding: FormControl<number | null>;
        boekwinst: FormControl<number | null>;
        afschrijvingspercentage: FormControl<number | null>;
        boekwaardeOpVervreemdingsmoment: FormControl<number | null>;
      }>
    >([]),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      tap(data => {
        this.jaren = [
          {value: data.year!, label: (data.year!).toString()},
          {value: data.year! - 1, label: (data.year! - 1).toString()},
          {value: data.year! - 2, label: (data.year! - 2).toString()},
          {value: data.year! - 3, label: (data.year! - 3).toString()},
          {value: data.year! - 4, label: (data.year! - 4).toString()},
          {value: data.year! - 5, label: (data.year! - 5).toString()}
        ];
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly indexOnderneming$: Observable<number> =
    this.dataService.indexOnderneming$.pipe(
      tap(data => {
        this.indexOnderneming = data;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.indexOnderneming$.subscribe();
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();
    });
  }

  get specificatie(): FormArray<FormGroup<{
    omschrijving: FormControl<string | null>;
    jaarVanVervreemding: FormControl<number | null>;
    boekwinst: FormControl<number | null>;
    afschrijvingspercentage: FormControl<number | null>;
    boekwaardeOpVervreemdingsmoment: FormControl<number | null>;
  }>> {
    return this.specificatieFg.controls.specHerinvesteringsreserve;
  }

  addRij(): void {
    this.specificatie.push(this.createFormGroup(), { emitEvent: false });
  }

  verwijderRij(index: number): void {
    this.specificatie.removeAt(index, { emitEvent: false });
    this.saveForm();
    this.totaalVrijval;
  }

  createFormGroup(item?: Herinvesteringsreserve) {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null, Validators.required),
      jaarVanVervreemding: new FormControl<number | null>(item?.jaarVanVervreemding ?? null, Validators.required),
      boekwinst: new FormControl<number | null>(item?.boekwinst ?? null, Validators.required),
      afschrijvingspercentage: new FormControl<number | null>(item?.afschrijvingspercentage ?? null, Validators.required),
      boekwaardeOpVervreemdingsmoment: new FormControl<number | null>(item?.boekwaardeOpVervreemdingsmoment ?? null, Validators.required),
    });
  }

  get totaalVrijval(): any {
    const opening = this.formGroup.get('waardeOpening')?.value ?? 0;
    const dotatie = this.formGroup.get('dotatieHIR')?.value ?? 0;
    const slot = this.formGroup.get('waardeSlot')?.value ?? 0;
    const vrijval = (opening + dotatie - slot) * -1;
    this.formGroup.patchValue({vrijvalHIR: vrijval}, {emitEvent: false});
    return vrijval;
  }

  showDialog() {
    this.handleSwitch(this.dataService.incomeTaxEntity.value, this.isPartner);
    this.dialogVisible = true;
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: any): void {
    this.resetFormGroup();
    const ondernemingen: Onderneming[] = details.box1?.winstUitOnderneming?.ondernemingen || [];
    this.patchFormGroups(ondernemingen);
  }

  private resetFormGroup() {
    this.formGroup.reset({}, {emitEvent: false})
  }

  private patchFormGroups(ondernemingen: Onderneming[]) {
    this.formGroup.reset({}, {emitEvent: false});
    const onderneming = ondernemingen[this.indexOnderneming];
    if (!onderneming) return;
    this.formGroup.patchValue({dotatieHIR: onderneming.dotatieHIR}, {emitEvent: false});
    this.formGroup.patchValue({vrijvalHIR: onderneming.vrijvalHIR}, {emitEvent: false});
    if (onderneming.eigenVermogen?.at(2)) {
      this.formGroup.patchValue({waardeOpening: onderneming.eigenVermogen[2].waardeOpening}, {emitEvent: false});
      this.formGroup.patchValue({waardeSlot: onderneming.eigenVermogen[2].waardeSlot}, {emitEvent: false});
    }
    if (onderneming.specificatieHerinvesteringsreserve && onderneming.specificatieHerinvesteringsreserve.length > 0) {
      this.specificatieFg.reset({}, {emitEvent: false});
      this.updateForm(onderneming.specificatieHerinvesteringsreserve);
    }
  }

  ngOnDestroy(): void {
    // rely on saveFrom in component
  }

  private updateForm(specs: Herinvesteringsreserve[]): void {
    const lijst = specs ?? [];
    const formArray = this.specificatie;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        formArray.at(i).patchValue(lijst[i], {emitEvent: false});
      } else {
        formArray.push(this.createFormGroup(lijst[i]), {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    const idx = this.indexOnderneming;
    const formValue = this.formGroup.getRawValue();
    const formLijst = this.specificatieFg.getRawValue();

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }
      const update = (aangifte: any) => {
        const existingOndernemingen = aangifte.box1?.winstUitOnderneming?.ondernemingen || [];
        const onderneming = existingOndernemingen[idx] || {};

        // Clone or create the array
        const reserve = Array.isArray(onderneming.eigenVermogen)
          ? [...onderneming.eigenVermogen]
          : [];
        // Ensure the array has at least 2 items
        while (reserve.length < 2) {
          reserve.push({});
        }
        // Update the second item
        reserve[1] = formValue;

        const updatedOnderneming: Partial<Onderneming> = {
          ...onderneming,
          dotatieHIR: (formValue.dotatieHIR ?? 0) > 1 ? formValue.dotatieHIR : undefined,
          vrijvalHIR: (formValue.vrijvalHIR ?? 0) > 1 ? formValue.vrijvalHIR : undefined,
          specificatieHerinvesteringsreserve: formLijst.specHerinvesteringsreserve.length > 0 ? formLijst.specHerinvesteringsreserve : undefined,
          beginstandHIR: [{omschrijving: 'Beginstand', bedrag: formValue.waardeOpening}],
        };
        // Clean all empty arrays, arrays of empty objects, and empty objects from the outgoing data
        const cleanedOnderneming = this.dataService.deepClean(updatedOnderneming);
        const updatedOndernemingen = existingOndernemingen.map((o: Onderneming, i: number) => i === idx ? cleanedOnderneming : o);
        return {
          ...aangifte,
          box1: {
            ...aangifte.box1,
            winstUitOnderneming: {
              ...aangifte.box1?.winstUitOnderneming,
              ondernemingen: updatedOndernemingen,
            },
          },
        };
      };
      const updatedAangifte = this.isPartner
        ? {
          ...currentEntity.aangifte,
          partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
        }
        : update(currentEntity.aangifte);
      return this.dataService.deepClean({
        ...currentEntity,
        aangifte: updatedAangifte,
      }) as IncomeTaxEntity;
    });
  }
}
